import React from 'react';
import VisaPageLayout, { FaqItem, ProcessStep, RequirementItem, VisaPlan } from './visa-page-layout';
import { useVisaPacksByCountry } from '../hooks/useVisaPacksByCountry';


const plans: VisaPlan[] = [
  { id: 'qa-30', name: '30-Day Entry Permit', entryType: 'Single Entry', validity: 'Use within 30 days of issue', stay: 'Up to 30 days in Qatar', processing: '1–3 business days', price: '0', currency: 'QAR' },
  { id: 'qa-96h', name: '96-Hour Transit Permit', entryType: 'Single Entry', validity: 'Aligned to your layover dates', stay: 'Up to 96 hours in Qatar', processing: 'Same day', price: '0', currency: 'QAR' },
  { id: 'qa-assist', name: 'Extended Stay — Assisted Application', entryType: 'Single Entry', validity: 'Use within 30 days of issue', stay: 'Up to 30 days, extendable on request', processing: '2–4 business days', price: '150', currency: 'QAR', featured: true },
];

const requirements: RequirementItem[] = [
  { label: 'Passport copy', detail: 'Valid for at least 6 months from your arrival date.' },
  { label: 'Passport-size photo', detail: 'Recent, plain background, only needed for the assisted extended-stay route.' },
  { label: 'Confirmed return ticket', detail: 'Round-trip or onward booking showing your departure from Qatar.' },
  { label: 'Hotel booking', detail: 'Confirmed accommodation for the length of your stay.' },
  { label: 'Layover itinerary (for transit)', detail: 'Connecting flight details showing your onward destination and layover length.' },
];

const goodToKnow: string[] = [
  'Many nationalities receive a free entry permit on arrival in Qatar — our assisted service is for travelers who want the paperwork double-checked in advance, or who need the extended-stay route.',
  'Qatar\'s free transit visa suits travelers with a long layover through Hamad International Airport.',
  'Modest dress is expected in public spaces, including malls and government buildings.',
  'Alcohol is only served in licensed hotels and venues — worth noting for travelers unfamiliar with local rules.',
];

const steps: ProcessStep[] = [
  { title: 'Tell us your travel plan', text: 'Share your passport nationality, dates and whether you\'re transiting or staying.' },
  { title: 'We confirm your entry route', text: 'We check whether you qualify for the free permit or need the assisted application.' },
  { title: 'We handle the paperwork', text: 'For the assisted route, our team files your documents and tracks the outcome.' },
  { title: 'You travel with confidence', text: 'Arrive knowing your entry permit or eVisa is already confirmed and in hand.' },
];

const faqs: FaqItem[] = [
  { question: 'Do I need to apply for a visa before flying to Qatar?', answer: 'Many nationalities don\'t — Qatar issues a free entry permit on arrival for eligible passports. Tell us your nationality and travel dates and we\'ll confirm exactly what applies to you.' },
  { question: 'What is the assisted application service for, if entry is often free?', answer: 'It\'s for travelers who want their documents reviewed in advance, need a longer or extended stay, or simply prefer everything confirmed before departure.' },
  { question: 'Can I use my Qatar layover to see the city?', answer: 'Yes — the transit permit is designed for exactly that, letting eligible travelers leave the airport during a long layover.' },
  { question: 'What if I want to stay longer than my permit allows?', answer: 'Extensions can sometimes be arranged through Qatar\'s immigration authority. Contact us before your permit expires and we\'ll advise on the options available.' },
];

export default function VisaQatarPage() {
    const { plans, loading, error } = useVisaPacksByCountry('QAT');
  return (
    <VisaPageLayout
      countryName="Qatar"
      flagEmoji="🇶🇦"
      heroEyebrow="State of Qatar"
      heroImage="https://images.unsplash.com/photo-1748373448914-1d7f882700e2?w=1600&q=80&fit=crop&crop=center"
      heroTitleLead="Your"
      heroTitleHighlight="Qatar"
      heroTitleTail="entry, confirmed before you fly"
      heroSubtitle="Whether it's a long layover in Doha or a full stay, we confirm your Qatar entry requirements and handle any paperwork so there are no surprises at the airport."
      trustStats={[
        { value: 'Same day', label: 'Transit Option' },
        { value: '96%', label: 'Approval Rate' },
        { value: '2,300+', label: 'Qatar Travelers Assisted' },
      ]}
      plans={plans}
       plansLoading={loading}
      plansError={error}
      requirements={requirements}
      goodToKnow={goodToKnow}
      steps={steps}
      faqs={faqs}
      ctaSubtitle="Tell us your travel dates and nationality — we'll confirm your entry route and handle the rest."
    />
  );
}